import { AtSign } from 'lucide-react';
import { useChannelQuotas } from '@/features/channel-quota/model/use-channel-quotas';
import { ChannelSettingsPage } from '@/features/marketing-shared/ui/channel-settings-page';
import { EmptyState } from '@/shared/components/empty-state';
import { usePassbook } from '@/shared/lib/passbook';
import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Card } from '@/shared/ui/card';
import { useEmailMarketing } from '../model/context';
import type { IdentityStatus } from '../model/types';

const IDENTITY_META: Record<IdentityStatus, { label: string; variant: NonNullable<BadgeProps['variant']> }> = {
  verified: { label: 'Verificado', variant: 'success' },
  pending: { label: 'Aguardando DNS', variant: 'warning' },
  failed: { label: 'Falha na verificação', variant: 'destructive' },
};

export function EmailSettingsPage() {
  const { idPassbook } = usePassbook();
  const { identities, identitiesState } = useEmailMarketing();
  const { quotas, live } = useChannelQuotas(idPassbook);

  return (
    <ChannelSettingsPage
      title="Envio"
      description="Domínio, remetente e cota de e-mail deste passbook."
      phase={identitiesState.phase}
      error={identitiesState.error}
      onRetry={identitiesState.refetch}
      loadingLabel="Carregando a configuração de envio"
      errorTitle="Não foi possível carregar o envio"
      capacity={quotas?.email}
      channel="email"
      capacityTitle="Saldo de e-mail"
      unit="e-mails"
      live={live}
    >
      {identities.length === 0 ? (
        <Card>
          <EmptyState
            icon={AtSign}
            title="Nenhuma identidade de envio"
            description="O remetente é liberado por loja pelo time da Fixe (domínio verificado no provedor). Sem identidade, a aprovação de campanha fica bloqueada."
          />
        </Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-3">
          {identities.map((identity) => {
            const meta = IDENTITY_META[identity.status];
            return (
              <Card key={identity.id} className="p-4">
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <h2 className="min-w-0 break-all font-medium">{identity.domain}</h2>
                  <Badge variant={meta.variant} className="shrink-0">{meta.label}</Badge>
                </div>
                <p className="mt-3 break-all text-sm">
                  {identity.fromName} &lt;{identity.fromEmail}&gt;
                </p>
                <p className="mt-2 text-sm text-muted-foreground">{identity.detail}</p>
              </Card>
            );
          })}
        </div>
      )}
    </ChannelSettingsPage>
  );
}
